import {
  QueryClient,
  useQuery,
  QueryClientProvider,
} from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import React from "react"
import { FiinderApiContext, UserIdProvider } from "./fiinder-api-context"
import type { FiinderApi } from "./fiinder-api-types.ts"
import { TenantUserIdProvider } from "./tenant-user-id-provider"
import { PdfViewerProvider } from "@/components/ui/pdf-viewer/pdf-context"
import { WidgetOverlayProvider } from "@/components/ui/widget/widget-overlay-context"
import { rotateStorageIfVisitorChanged } from "@/lib/visitor-scoped-storage"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
})

const PERSIST_CACHE_KEY = import.meta.env.DEV
  ? "fiinder_dev_react_query_cache"
  : "fiinder_react_query_cache"

// Module-scope: detect visitor rotation BEFORE persister is created.
// Running this at module-load (not in useMemo) guarantees the order:
//   1. rotation sweeps visitor-scoped fiinder_* keys
//   2. createBrowserPersister() instantiates the persister
//   3. if rotation detected, persister.removeClient() drops the
//      TanStack persisted entry synchronously (sync storage adapter)
//   4. only then PersistQueryClientProvider mounts and hydrates
const _visitorChanged = typeof window !== "undefined"
  ? rotateStorageIfVisitorChanged(window.FIINDER_TENANT_USER_ID)
  : false

const createBrowserPersister = () => {
  if (typeof window === "undefined") return undefined
  try {
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
      key: PERSIST_CACHE_KEY,
    })
    if (_visitorChanged) {
      // Sync storage adapter: removeClient() returns a settled promise
      // because the underlying localStorage.removeItem is synchronous.
      void persister.removeClient()
    }
    return persister
  } catch (error) {
    console.warn("[ApiProvider] Failed to access localStorage for persistence", error)
    return undefined
  }
}

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
)

export function ApiProvider(props: {
  children: React.ReactNode
  api: FiinderApi
}) {
  const [showDevtools, setShowDevtools] = React.useState(false)
  const persister = React.useMemo(() => createBrowserPersister(), [])

  React.useEffect(() => {
    // @ts-expect-error
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  const childrenWithDevtools = (
    <>
      {props.children}
      {showDevtools && (
        <React.Suspense fallback={<div>Loading...</div>}>
          <ReactQueryDevtoolsProduction />
        </React.Suspense>
      )}
    </>
  )

  if (!persister) {
    return (
      <FiinderApiContext.Provider value={props.api}>
        <UserIdProvider>
          <QueryClientProvider client={queryClient}>
            <TenantUserIdProvider>{childrenWithDevtools}</TenantUserIdProvider>
          </QueryClientProvider>
        </UserIdProvider>
      </FiinderApiContext.Provider>
    )
  }

  return (
    <FiinderApiContext.Provider value={props.api}>
      <UserIdProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{ persister }}
        >
          <TenantUserIdProvider>{childrenWithDevtools}</TenantUserIdProvider>
        </PersistQueryClientProvider>
      </UserIdProvider>
    </FiinderApiContext.Provider>
  )
}

export function TestApiProvider(props: {
  children: React.ReactNode
  api: FiinderApi
}) {
  return (
    <FiinderApiContext.Provider value={props.api}>
      <UserIdProvider>
        <WidgetOverlayProvider>
          <PdfViewerProvider>
            <QueryClientProvider client={queryClient}>
              <TenantUserIdProvider>{props.children}</TenantUserIdProvider>
            </QueryClientProvider>
          </PdfViewerProvider>
        </WidgetOverlayProvider>
      </UserIdProvider>
    </FiinderApiContext.Provider>
  )
}

/** Clear all cached queries - useful for test isolation */
export function clearQueryCache() {
  queryClient.clear()
}

export function TanStackExample() {
  const { isPending, error, data } = useQuery({
    queryKey: ["repoData"],
    staleTime: 0,
    queryFn: () =>
      // fetch("https://api.github.com/repos/TanStack/query").then((res) =>
      //   res.json()
      // ),
      fetch("https://api.github.com/repos/TanStack/query").then((res) =>
        res.json()
      ),
  })

  if (isPending) return "Loading..."

  if (error) return "An error has occurred: " + error.message

  return (
    <div>
      <h1>{data.name}</h1>
      <p>{data.description}</p>
      <strong>👀 {data.subscribers_count}</strong>{" "}
      <strong>✨ {data.stargazers_count}</strong>{" "}
      <strong>🍴 {data.forks_count}</strong>
    </div>
  )
}
